import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api from "@/api/client";

export interface AuthUser {
  user_id: number;
  line_display_name: string | null;
  line_picture_url: string | null;
  requires_consent: boolean;
  requires_profile: boolean;
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<AuthUser | null>(null);
  const jwt = ref<string | null>(localStorage.getItem("jwt"));

  const isLoggedIn = computed(() => !!jwt.value);

  function logout() {
    jwt.value = null;
    user.value = null;
    localStorage.removeItem("jwt");
  }

  async function loginWithLine(accessToken: string) {
    const { data } = await api.post("/auth/line", { access_token: accessToken });
    const { jwt: token, ...userData } = data;
    jwt.value = token;
    localStorage.setItem("jwt", token);
    user.value = userData as AuthUser;
    return data;
  }

  async function consent() {
    await api.post("/auth/consent", { consented: true });
    if (user.value) user.value.requires_consent = false;
  }

  /** Hydrate `user` from /api/me when JWT is in localStorage but user is null
   * (e.g., after page reload). Returns true if hydrated. On 401 (stale/revoked
   * JWT), clears the JWT so the router guard redirects to home. */
  async function fetchMe(): Promise<boolean> {
    if (!jwt.value) return false;
    try {
      const { data } = await api.get("/me");
      user.value = {
        user_id: data.user.id,
        line_display_name: data.user.line_display_name,
        line_picture_url: data.user.line_picture_url,
        requires_consent: !data.user.consented_at,
        requires_profile:
          !data.user.name || !data.user.phone || !data.user.email,
      };
      return true;
    } catch (e: any) {
      if (e?.response?.status === 401) {
        // Stale or revoked JWT — clear local state so router guard sends user home.
        logout();
      }
      return false;
    }
  }

  return { user, jwt, isLoggedIn, loginWithLine, consent, fetchMe, logout };
});
